import React from 'react'
import { StaticQuery, graphql } from 'gatsby'
import { Helmet } from 'react-helmet'

// See here for favicon sizes reasoning
// https://www.emergeinteractive.com/insights/detail/the-essentials-of-favicons/
// generics
import favicon32 from '../images/npf-logo-32.png'
import favicon128 from '../images/npf-logo-128.png'
import favicon144 from '../images/npf-logo-144.png'
import favicon192 from '../images/npf-logo-192.png'
// android
import favicon196 from '../images/npf-logo-196.png'
// ios
import favicon152 from '../images/npf-logo-152.png'
import favicon167 from '../images/npf-logo-167.png'
import favicon180 from '../images/npf-logo-180.png'

const FALLBACK_SOCIAL_IMAGE_QUERY = graphql`
  {
    prismicGeneralSiteSettings {
      data {
        fallback_twitter_share_image {
          url
          alt
        }
        fallback_open_graph_share_image {
          alt
          url
        }
      }
    }
  }
`

const SEO = ({ seo }) => (
  <StaticQuery
    query={`${FALLBACK_SOCIAL_IMAGE_QUERY}`}
    render={data => {
      const document = data?.prismicGeneralSiteSettings?.data
      const twitter_fallback_url = document.fallback_twitter_share_image.url
      const og_fallback_url = document.fallback_open_graph_share_image.url
      return (
        <Helmet
          htmlAttributes={{
            lang: 'en',
          }}
          title={seo.title ? `${seo.title}` : 'National Psoriasis Foundation'}
          link={[
            // generic
            {
              rel: 'icon',
              type: 'image/png',
              sizes: '32x32',
              href: `${favicon32}`,
            },
            {
              rel: 'icon',
              type: 'image/png',
              sizes: '128x128',
              href: `${favicon128}`,
            },
            {
              rel: 'icon',
              type: 'image/png',
              sizes: '144x144',
              href: `${favicon144}`,
            },
            {
              rel: 'icon',
              type: 'image/png',
              sizes: '192x192',
              href: `${favicon192}`,
            },
            // android
            {
              rel: 'shortcut icon',
              type: 'image/png',
              sizes: '196x196',
              href: `${favicon196}`,
            },
            // ios
            {
              rel: 'apple-touch-icon',
              type: 'image/png',
              sizes: '152x152',
              href: `${favicon152}`,
            },
            {
              rel: 'apple-touch-icon',
              type: 'image/png',
              sizes: '167x167',
              href: `${favicon167}`,
            },
            {
              rel: 'apple-touch-icon',
              type: 'image/png',
              sizes: '180x180',
              href: `${favicon180}`,
            },
          ]}
        >
          <meta charSet="utf-8" />
          {seo.description && (
            <meta name="description" content={seo.description} />
          )}
          {seo.title && <meta name="title" content={seo.title} />}
          {seo.keywords && <meta name="keywords" content={seo.keywords} />}

          {seo.isExcluded && <meta name="robots" content="noindex" />}

          {/* opengraph */}
          {seo.title && <meta property="og:title" content={seo.title} />}
          {seo.description && (
            <meta property="og:description" content={seo.description} />
          )}
          <meta
            name="image"
            property="og:image"
            content={seo.facebook_image?.url?.split('?')[0] ?? og_fallback_url}
          />

          {/* Twitter */}
          <meta name="twitter:site" content="@npf" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:creator" content="@npf" />
          {seo.title && <meta property="twitter:title" content={seo.title} />}
          <meta
            property="twitter:image"
            content={seo.twitter_image?.url ?? twitter_fallback_url}
          />

          {/* Schema embed from Prismic */}
          {seo.seo_embed_code && (
            <script type="application/ld+json">
              {`
              ${seo.seo_embed_code}
            `}
            </script>
          )}
        </Helmet>
      )
    }}
  />
)

export default SEO
